// src/pages/ia-paca/carte-entreprises.tsx
//
// Carte interactive des entreprises IA en PACA — différenciation forte
// vs cabinets concurrents. Data inline (35 entreprises) compilée à partir
// d'observations terrain et sources publiques. Migrable vers Supabase
// dans un sprint ultérieur si besoin de mise à jour fréquente.

import { useEffect, useMemo, useRef, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import Layout from '../../components/layout/layout';
import IaPacaCTA from '../../components/ia-paca/IaPacaCTA';
import {
  ArrowLeft,
  Building2,
  Filter,
  MapPin,
  Users,
  ExternalLink,
} from 'lucide-react';

// =========================================================================
// Data : entreprises IA en PACA
// =========================================================================

type EntrepriseCategorie =
  | 'corporate'
  | 'scaleup'
  | 'pme-tech'
  | 'startup'
  | 'recherche'
  | 'service-public';

interface Entreprise {
  nom: string;
  ville: string;
  lat: number;
  lng: number;
  categorie: EntrepriseCategorie;
  effectif: string; // ex "5 000+", "50-100", etc.
  pitch: string;
  stack?: string[]; // ce qu'on sait de leur stack/usage IA
  site?: string;
  hub?: string; // Sophia / La Coque / etc.
}

// 35 entreprises (échantillon représentatif, non exhaustif)
const ENTREPRISES: Entreprise[] = [
  // -- MARSEILLE / AMP --
  {
    nom: 'CMA CGM',
    ville: 'Marseille',
    lat: 43.2776,
    lng: 5.3601,
    categorie: 'corporate',
    effectif: '155 000+',
    pitch: 'Géant du shipping. Partenariat 100 M€ avec Mistral AI annoncé fin 2024. Recrute fortement sur ML maritime, optimisation routing, computer vision portuaire.',
    stack: ['Mistral AI', 'Computer Vision', 'Time Series', 'MLOps'],
    site: 'https://www.cmacgm-group.com',
    hub: 'Marseille',
  },
  {
    nom: 'Withings',
    ville: 'Marseille',
    lat: 43.2965,
    lng: 5.3698,
    categorie: 'scaleup',
    effectif: '300+',
    pitch: 'HealthTech. Bureau Marseille R&D. IA santé : détection anomalies cardiaques, qualité du sommeil, signaux biométriques.',
    stack: ['PyTorch', 'Edge ML', 'Time Series'],
    site: 'https://www.withings.com',
    hub: 'Marseille',
  },
  {
    nom: 'Onepilot',
    ville: 'Marseille',
    lat: 43.2950,
    lng: 5.3760,
    categorie: 'scaleup',
    effectif: '100+',
    pitch: "Customer support augmenté par IA. Plateforme combine humains + LLM. Lève en série A en 2024, embauche à Marseille.",
    stack: ['LLM', 'RAG', 'LangChain', 'OpenAI'],
    site: 'https://www.onepilot.co',
    hub: 'Marseille',
  },
  {
    nom: 'JetPack Data',
    ville: 'Marseille',
    lat: 43.3010,
    lng: 5.3800,
    categorie: 'startup',
    effectif: '10-30',
    pitch: 'Data engineering + IA pour scale-ups. Bureau La Coque. Focus dbt, BigQuery, ingestion automatisée.',
    stack: ['dbt', 'BigQuery', 'Python', 'LLM'],
    hub: 'La Coque',
  },
  {
    nom: 'Pandacraft',
    ville: 'Marseille',
    lat: 43.2900,
    lng: 5.3800,
    categorie: 'scaleup',
    effectif: '100+',
    pitch: 'EdTech (kits éducatifs enfants). Intègre IA dans la personnalisation des parcours et la génération de contenu.',
    stack: ['LLM', 'Personnalisation', 'Recommandation'],
    site: 'https://www.pandacraft.co',
    hub: 'Marseille',
  },
  {
    nom: 'iAdvize',
    ville: 'Marseille',
    lat: 43.2920,
    lng: 5.3700,
    categorie: 'scaleup',
    effectif: '200+',
    pitch: 'Conversational commerce. Bureau Marseille. Agents IA pour le e-commerce.',
    stack: ['LLM', 'RAG', 'Conversational AI'],
    site: 'https://www.iadvize.com',
    hub: 'Marseille',
  },
  {
    nom: 'Voodoo',
    ville: 'Marseille',
    lat: 43.2950,
    lng: 5.3650,
    categorie: 'scaleup',
    effectif: '700+',
    pitch: 'Mobile gaming leader. Bureau Marseille. ML pour matching, monétisation, anti-fraude.',
    stack: ['ML', 'Recommandation', 'AdTech'],
    site: 'https://www.voodoo.io',
    hub: 'Marseille',
  },
  {
    nom: 'AIM 2026',
    ville: 'Marseille',
    lat: 43.2990,
    lng: 5.3750,
    categorie: 'service-public',
    effectif: '50+',
    pitch: "AI Marseille — événement structurant + initiatives écosystème IA Marseille. Hub La Coque.",
    hub: 'La Coque',
  },
  {
    nom: 'P.Factory',
    ville: 'Marseille',
    lat: 43.3050,
    lng: 5.3700,
    categorie: 'service-public',
    effectif: '20+',
    pitch: 'Accélérateur du Pays d\'Aix-Marseille pour startups deeptech / IA / IoT.',
    hub: 'Marseille',
  },
  {
    nom: 'Belle de Mai',
    ville: 'Marseille',
    lat: 43.3160,
    lng: 5.3900,
    categorie: 'service-public',
    effectif: '50+',
    pitch: 'Pôle Média Belle de Mai — incubateur historique Marseille, IA appliquée au média et culture.',
    hub: 'Marseille',
  },

  // -- AIX-EN-PROVENCE & alentours --
  {
    nom: 'Cybelius',
    ville: 'Aix-en-Provence',
    lat: 43.5240,
    lng: 5.4480,
    categorie: 'startup',
    effectif: '20-50',
    pitch: 'Cybersécurité + IA pour les infrastructures critiques. Maintenance prédictive, détection d\'anomalies.',
    stack: ['ML', 'Time Series', 'Anomaly Detection'],
    hub: 'Aix-en-Provence',
  },
  {
    nom: 'CEA Cadarache',
    ville: 'Saint-Paul-lez-Durance',
    lat: 43.6900,
    lng: 5.7700,
    categorie: 'recherche',
    effectif: '5 000+',
    pitch: 'Centre de recherche nucléaire. ITER + programmes énergie. Cas d\'usage IA : simulation, contrôle de plasma, optimisation.',
    stack: ['Simulation', 'Optimisation', 'ML Physics'],
    site: 'https://www.cea.fr',
    hub: 'Cadarache',
  },
  {
    nom: 'Airbus Helicopters',
    ville: 'Marignane',
    lat: 43.4360,
    lng: 5.2150,
    categorie: 'corporate',
    effectif: '10 000+',
    pitch: 'Hélicoptères. Cas d\'usage IA : maintenance prédictive, computer vision contrôle qualité, jumeau numérique.',
    stack: ['Computer Vision', 'Predictive Maintenance', 'Digital Twin'],
    site: 'https://www.airbus.com',
    hub: 'Aix-en-Provence',
  },
  {
    nom: 'STMicroelectronics Rousset',
    ville: 'Rousset',
    lat: 43.4690,
    lng: 5.6230,
    categorie: 'corporate',
    effectif: '2 500+',
    pitch: 'Semi-conducteurs sécurisés. IA appliquée au contrôle qualité wafers, optimisation procédés, edge AI.',
    stack: ['Computer Vision', 'Edge AI', 'Manufacturing'],
    hub: 'Rousset',
  },
  {
    nom: 'GEMALTO / Thales DIS Gemenos',
    ville: 'Gémenos',
    lat: 43.2980,
    lng: 5.6320,
    categorie: 'corporate',
    effectif: '1 000+',
    pitch: 'Sécurité digitale, identité numérique. IA pour la détection de fraude, biométrie, document analysis.',
    stack: ['Computer Vision', 'Biometrics', 'Fraud Detection'],
    hub: 'Gémenos',
  },

  // -- SOPHIA ANTIPOLIS --
  {
    nom: 'Amadeus',
    ville: 'Sophia Antipolis',
    lat: 43.6190,
    lng: 7.0510,
    categorie: 'corporate',
    effectif: '17 000+',
    pitch: 'Travel tech mondial. Siège tech à Sophia. Forte équipe IA : recommandation, NLP voyage, pricing, fraud detection.',
    stack: ['ML', 'NLP', 'Pricing', 'Recsys'],
    site: 'https://www.amadeus.com',
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'IBM France Lab Sophia',
    ville: 'Sophia Antipolis',
    lat: 43.6160,
    lng: 7.0490,
    categorie: 'corporate',
    effectif: '500+',
    pitch: 'IBM Research France. AI for Operations, Watson, recherche LLM appliquée.',
    stack: ['Watson', 'LLM', 'AIOps'],
    site: 'https://www.ibm.com',
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'Renesas Electronics',
    ville: 'Sophia Antipolis',
    lat: 43.6210,
    lng: 7.0530,
    categorie: 'corporate',
    effectif: '500+',
    pitch: 'Semi-conducteurs automobile/IoT. R&D IA embarquée, ML pour systèmes contraints.',
    stack: ['Edge AI', 'Embedded ML', 'Automotive'],
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'Toyota Connected Europe',
    ville: 'Sophia Antipolis',
    lat: 43.6170,
    lng: 7.0490,
    categorie: 'corporate',
    effectif: '200+',
    pitch: 'Toyota Connected. IA pour véhicules connectés, prédiction comportement, telematics.',
    stack: ['ML', 'Time Series', 'Automotive'],
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'Symphony Retail AI',
    ville: 'Sophia Antipolis',
    lat: 43.6200,
    lng: 7.0510,
    categorie: 'corporate',
    effectif: '500+',
    pitch: 'AI for Retail. Pricing, supply chain, recommandation. Marque IA dans le nom.',
    stack: ['ML', 'Forecasting', 'Optimization'],
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'Texas Instruments',
    ville: 'Villeneuve-Loubet',
    lat: 43.6500,
    lng: 7.1200,
    categorie: 'corporate',
    effectif: '500+',
    pitch: 'Semi-conducteurs. Centre R&D France à Villeneuve-Loubet. IA embarquée, signal processing.',
    stack: ['Embedded ML', 'Signal Processing', 'IoT'],
    hub: 'Côte d\'Azur',
  },
  {
    nom: 'INRIA Sophia',
    ville: 'Sophia Antipolis',
    lat: 43.6160,
    lng: 7.0760,
    categorie: 'recherche',
    effectif: '1 000+',
    pitch: 'Centre Inria majeur. Vivier historique IA + spin-offs deeptech. Computer vision, ML théorique, NLP.',
    stack: ['Recherche IA fondamentale', 'Computer Vision', 'NLP'],
    site: 'https://www.inria.fr',
    hub: 'Sophia Antipolis',
  },
  {
    nom: '3IA Côte d\'Azur',
    ville: 'Sophia Antipolis',
    lat: 43.6155,
    lng: 7.0720,
    categorie: 'recherche',
    effectif: '200+',
    pitch: 'Institut interdisciplinaire IA (un des 4 français). 200 chercheurs sur 5 axes (santé, environnement, sciences, biologie, smart territory).',
    stack: ['Recherche IA appliquée', 'Multi-disciplinaire'],
    site: 'https://3ia.univ-cotedazur.fr',
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'EURECOM',
    ville: 'Sophia Antipolis',
    lat: 43.6155,
    lng: 7.0780,
    categorie: 'recherche',
    effectif: '200+',
    pitch: 'École d\'ingénieurs et centre de recherche. IA + Cybersécurité + Télécoms. Source de profils techniques pointus.',
    stack: ['IA', 'Cybersec', 'Telecom'],
    site: 'https://www.eurecom.fr',
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'Pôle Alpha',
    ville: 'Sophia Antipolis',
    lat: 43.6150,
    lng: 7.0500,
    categorie: 'service-public',
    effectif: '50+',
    pitch: '8 500 m² inaugurés en janvier 2026. Réunit incubateurs, House of AI, Cyber Campus, écoles.',
    hub: 'Sophia Antipolis',
  },
  {
    nom: 'BPCE-IT',
    ville: 'Sophia Antipolis',
    lat: 43.6180,
    lng: 7.0540,
    categorie: 'corporate',
    effectif: '500+',
    pitch: 'IT du groupe BPCE (banques populaires/caisses d\'épargne). Plateforme banque digitale, IA pour scoring/fraude/relation client.',
    stack: ['ML', 'Banking', 'Fraud Detection', 'NLP'],
    hub: 'Sophia Antipolis',
  },

  // -- NICE / CÔTE D'AZUR --
  {
    nom: 'IMREDD',
    ville: 'Nice',
    lat: 43.6970,
    lng: 7.2620,
    categorie: 'recherche',
    effectif: '50+',
    pitch: 'Institut Méditerranéen du Risque, Environnement, Développement Durable. Smart city, IA appliquée à l\'eau, l\'énergie, la mobilité urbaine.',
    stack: ['Smart City', 'IoT', 'Time Series'],
    hub: 'Nice',
  },
  {
    nom: 'CHU Nice',
    ville: 'Nice',
    lat: 43.7050,
    lng: 7.2680,
    categorie: 'service-public',
    effectif: '8 000+',
    pitch: 'CHU Nice + Centre Antoine Lacassagne. IA santé : imagerie médicale, oncologie computationnelle, prédiction de pathologies.',
    stack: ['Medical Imaging', 'Healthcare AI'],
    hub: 'Nice',
  },
  {
    nom: 'Université Côte d\'Azur (UniCA)',
    ville: 'Nice',
    lat: 43.6800,
    lng: 7.2050,
    categorie: 'recherche',
    effectif: '5 000+',
    pitch: '30 000 étudiants. Labellisée Université d\'Excellence. Masters IA, Data Science. Source de talents IA juniors.',
    stack: ['Formation IA', 'Recherche'],
    hub: 'Nice',
  },
  {
    nom: 'EDHEC Business School',
    ville: 'Nice',
    lat: 43.6580,
    lng: 7.2470,
    categorie: 'recherche',
    effectif: '500+',
    pitch: 'Campus Nice EDHEC. Spécialisation finance et data, fort lien avec la FinTech Monaco.',
    stack: ['Data Science Finance'],
    hub: 'Nice',
  },

  // -- TOULON / VAR --
  {
    nom: 'Naval Group',
    ville: 'Toulon',
    lat: 43.1300,
    lng: 5.9400,
    categorie: 'corporate',
    effectif: '15 000+',
    pitch: 'Naval défense. Cas d\'usage IA : sonar, anomaly detection, maintenance prédictive, jumeau numérique navires.',
    stack: ['Computer Vision', 'Signal Processing', 'MLOps'],
    site: 'https://www.naval-group.com',
    hub: 'Toulon',
  },
  {
    nom: 'DGA Toulon (Direction Générale Armement)',
    ville: 'Toulon',
    lat: 43.1100,
    lng: 5.9300,
    categorie: 'service-public',
    effectif: '500+',
    pitch: 'IA défense/sécurité, R&D souveraine, fortes opportunités pour profils habilités.',
    stack: ['IA Défense', 'Sécurité'],
    hub: 'Toulon',
  },
  {
    nom: 'TVT Innovation',
    ville: 'Toulon',
    lat: 43.1240,
    lng: 5.9280,
    categorie: 'service-public',
    effectif: '20+',
    pitch: 'Accélérateur d\'innovation Toulon Var. Soutient startups IA + maritime + cybersec.',
    hub: 'Toulon',
  },
  {
    nom: 'Pôle SCS',
    ville: 'Sophia Antipolis',
    lat: 43.6180,
    lng: 7.0560,
    categorie: 'service-public',
    effectif: '20+',
    pitch: 'Pôle de compétitivité Solutions Communicantes Sécurisées. Couvre IoT, IA, cybersec sur tout le bassin PACA + Monaco.',
    hub: 'Sophia Antipolis',
  },
];

const CATEGORIE_LABELS: Record<EntrepriseCategorie, { label: string; color: string }> = {
  corporate: { label: 'Corporate / ETI', color: '#2563eb' },
  scaleup: { label: 'Scale-up', color: '#db2777' },
  'pme-tech': { label: 'PME tech', color: '#7c3aed' },
  startup: { label: 'Startup', color: '#f97316' },
  recherche: { label: 'Recherche / Université', color: '#059669' },
  'service-public': { label: 'Service public / Hub', color: '#64748b' },
};

// =========================================================================
// Composant carte
// =========================================================================

function EntreprisesMap({
  entreprises,
  onSelect,
}: {
  entreprises: Entreprise[];
  onSelect: (e: Entreprise) => void;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<any>(null);

  useEffect(() => {
    if (!containerRef.current) return;

    import('leaflet').then((L) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      delete (L.Icon.Default.prototype as any)._getIconUrl;
      L.Icon.Default.mergeOptions({
        iconRetinaUrl:
          'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png',
        iconUrl: 'https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png',
        shadowUrl:
          'https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png',
      });

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      // Centre PACA
      const map = L.map(containerRef.current!, {
        center: [43.6, 6.0],
        zoom: 8,
        scrollWheelZoom: true,
      });

      mapRef.current = map;

      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution:
          '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>',
      }).addTo(map);

      entreprises.forEach((e) => {
        const color = CATEGORIE_LABELS[e.categorie].color;
        const marker = L.circleMarker([e.lat, e.lng], {
          radius: 8,
          color: '#ffffff',
          fillColor: color,
          fillOpacity: 0.9,
          weight: 2,
        });

        const popup = L.popup({ maxWidth: 280 }).setContent(`
          <div style="font-size:13px;line-height:1.5;min-width:200px">
            <p style="font-weight:700;color:#0f172a;margin:0 0 2px;font-size:14px">${e.nom}</p>
            <p style="color:#64748b;margin:0 0 4px;font-size:11px">
              ${CATEGORIE_LABELS[e.categorie].label} · ${e.effectif} salariés
            </p>
            <p style="color:#475569;margin:0 0 8px;font-size:12px">📍 ${e.ville}</p>
            <p style="color:#334155;margin:0 0 8px;font-size:12px">${e.pitch}</p>
            ${
              e.site
                ? `<a href="${e.site}" target="_blank" rel="noopener noreferrer" style="color:#db2777;font-weight:500;text-decoration:none;font-size:12px">Site officiel ↗</a>`
                : ''
            }
          </div>
        `);

        marker.bindPopup(popup);
        marker.on('click', () => onSelect(e));
        marker.addTo(map);
      });
    });

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entreprises]);

  return (
    <>
      <link
        rel="stylesheet"
        href="https://unpkg.com/leaflet@1.9.4/dist/leaflet.css"
      />
      <div
        ref={containerRef}
        className="h-[500px] w-full rounded-2xl border border-slate-200 bg-slate-100 lg:h-[600px]"
      />
    </>
  );
}

// =========================================================================
// Page
// =========================================================================

export default function CarteEntreprisesIaPacaPage() {
  const [filterCategorie, setFilterCategorie] = useState<
    EntrepriseCategorie | 'all'
  >('all');
  const [filterVille, setFilterVille] = useState<string>('all');
  const [selected, setSelected] = useState<Entreprise | null>(null);

  const filtered = useMemo(() => {
    return ENTREPRISES.filter((e) => {
      if (filterCategorie !== 'all' && e.categorie !== filterCategorie) return false;
      if (filterVille !== 'all' && e.hub !== filterVille) return false;
      return true;
    });
  }, [filterCategorie, filterVille]);

  const hubs = useMemo(() => {
    const s = new Set(ENTREPRISES.map((e) => e.hub).filter(Boolean));
    return Array.from(s).sort();
  }, []);

  const jsonLd = {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    name: 'Carte des entreprises IA en PACA',
    description:
      "Carte interactive des entreprises, scale-ups, laboratoires et hubs IA en Provence-Alpes-Côte d'Azur. 35 acteurs référencés sur Marseille, Aix, Sophia Antipolis, Nice, Toulon.",
    url: 'https://recruteurs.io/ia-paca/carte-entreprises',
    numberOfItems: ENTREPRISES.length,
  };

  return (
    <Layout
      title="Carte interactive des entreprises IA en PACA — 35 acteurs | recruteurs.io"
      description="Carte interactive de 35 entreprises, scale-ups et laboratoires IA en Provence-Alpes-Côte d'Azur. Filtrez par ville, par catégorie. Découvrez l'écosystème IA local."
      keywords="carte entreprises IA PACA, écosystème IA Marseille, entreprises IA Sophia Antipolis, scale-ups IA Côte d'Azur, écosystème intelligence artificielle Provence"
      breadcrumbs={[
        { name: 'Accueil', href: '/' },
        { name: 'Recrutement IA PACA', href: '/ia-paca' },
        { name: 'Carte des entreprises', href: '/ia-paca/carte-entreprises' },
      ]}
    >
      <Helmet>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* Hero compact */}
      <section className="bg-gradient-to-br from-slate-50 via-white to-violet-50/30 py-10 lg:py-14">
        <div className="mx-auto max-w-6xl px-6">
          <Link
            to="/ia-paca"
            className="inline-flex items-center gap-1 text-xs font-medium text-slate-500 hover:text-slate-900"
          >
            <ArrowLeft className="h-3 w-3" />
            Retour au hub IA PACA
          </Link>

          <div className="mt-6 inline-flex items-center gap-1.5 rounded-full border border-violet-200 bg-white/80 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-violet-700 backdrop-blur">
            <MapPin className="h-3 w-3" />
            Cartographie · mise à jour mai 2026
          </div>

          <h1 className="mt-5 text-3xl font-bold leading-[1.1] tracking-tight text-slate-900 sm:text-4xl lg:text-5xl">
            La carte de l'écosystème{' '}
            <span className="bg-gradient-to-r from-pink-600 via-orange-500 to-violet-600 bg-clip-text text-transparent">
              IA en PACA
            </span>
            .
          </h1>
          <p className="mt-4 max-w-2xl text-base leading-relaxed text-slate-600 sm:text-lg">
            <strong>{ENTREPRISES.length} acteurs identifiés</strong> sur
            Marseille, Aix, Sophia Antipolis, Nice et Toulon. Corporates,
            scale-ups, startups, laboratoires, hubs. Filtrez par ville ou
            catégorie, cliquez sur un point pour découvrir l'entreprise.
          </p>
          <div className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-amber-50 px-2.5 py-1 text-[11px] font-medium text-amber-800 ring-1 ring-amber-200">
            Cartographie non exhaustive — éditée par Recruteurs.io. Une suggestion ?{' '}
            <a
              href="mailto:contact@recruteurs.io"
              className="font-semibold underline"
            >
              contact@recruteurs.io
            </a>
          </div>
        </div>
      </section>

      {/* Carte + filtres */}
      <section className="bg-white py-10 lg:py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div className="grid grid-cols-1 gap-6 lg:grid-cols-4">
            {/* Filtres */}
            <aside className="lg:col-span-1">
              <div className="sticky top-6 space-y-5">
                <div className="rounded-2xl border border-slate-200 bg-white p-4">
                  <div className="flex items-center gap-2">
                    <Filter className="h-4 w-4 text-violet-600" />
                    <h3 className="text-sm font-bold text-slate-900">Filtres</h3>
                  </div>

                  <div className="mt-4">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Catégorie
                    </label>
                    <div className="mt-2 space-y-1">
                      <FilterChip
                        active={filterCategorie === 'all'}
                        onClick={() => setFilterCategorie('all')}
                        label={`Toutes (${ENTREPRISES.length})`}
                        color="#64748b"
                      />
                      {(Object.keys(CATEGORIE_LABELS) as EntrepriseCategorie[]).map(
                        (cat) => {
                          const count = ENTREPRISES.filter((e) => e.categorie === cat).length;
                          return (
                            <FilterChip
                              key={cat}
                              active={filterCategorie === cat}
                              onClick={() => setFilterCategorie(cat)}
                              label={`${CATEGORIE_LABELS[cat].label} (${count})`}
                              color={CATEGORIE_LABELS[cat].color}
                            />
                          );
                        },
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <label className="block text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                      Ville / hub
                    </label>
                    <select
                      value={filterVille}
                      onChange={(e) => setFilterVille(e.target.value)}
                      className="mt-2 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm focus:border-violet-500 focus:outline-none focus:ring-2 focus:ring-violet-200"
                    >
                      <option value="all">Toutes les villes</option>
                      {hubs.map((h) => (
                        <option key={h} value={h}>
                          {h}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="mt-4 rounded-lg bg-violet-50 p-3 text-xs text-violet-900">
                    <strong>{filtered.length}</strong> entreprise
                    {filtered.length > 1 ? 's' : ''} affichée
                    {filtered.length > 1 ? 's' : ''} sur la carte.
                  </div>
                </div>

                {selected && (
                  <div className="rounded-2xl border-2 border-pink-200 bg-pink-50/50 p-4">
                    <div className="text-[10px] font-bold uppercase tracking-wider text-pink-700">
                      Sélection
                    </div>
                    <h3 className="mt-2 text-base font-bold text-slate-900">
                      {selected.nom}
                    </h3>
                    <p className="mt-1 text-[11px] text-slate-500">
                      {CATEGORIE_LABELS[selected.categorie].label} ·{' '}
                      {selected.effectif} salariés · {selected.ville}
                    </p>
                    <p className="mt-3 text-sm leading-relaxed text-slate-700">
                      {selected.pitch}
                    </p>
                    {selected.stack && (
                      <div className="mt-3 flex flex-wrap gap-1">
                        {selected.stack.map((s) => (
                          <span
                            key={s}
                            className="inline-flex items-center rounded-md bg-white px-2 py-0.5 text-[10px] font-medium text-slate-700 ring-1 ring-slate-200"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    )}
                    {selected.site && (
                      <a
                        href={selected.site}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="mt-3 inline-flex items-center gap-1 text-xs font-semibold text-pink-700 hover:underline"
                      >
                        Site officiel
                        <ExternalLink className="h-3 w-3" />
                      </a>
                    )}
                  </div>
                )}
              </div>
            </aside>

            {/* Carte */}
            <div className="lg:col-span-3">
              <EntreprisesMap entreprises={filtered} onSelect={setSelected} />
            </div>
          </div>
        </div>
      </section>

      {/* Liste tabulaire */}
      <section className="bg-slate-50 py-10 lg:py-14">
        <div className="mx-auto max-w-7xl px-6">
          <div className="text-[11px] font-bold uppercase tracking-[0.15em] text-violet-700">
            Liste complète
          </div>
          <h2 className="mt-2 text-2xl font-bold tracking-tight text-slate-900 sm:text-3xl">
            Les {filtered.length} acteurs en détail.
          </h2>
          <div className="mt-8 grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {filtered.map((e) => (
              <div
                key={e.nom}
                className="rounded-2xl border border-slate-200 bg-white p-5 transition hover:border-violet-300 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <h3 className="text-base font-bold text-slate-900">{e.nom}</h3>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
                      <span
                        className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-0.5 font-medium"
                        style={{
                          backgroundColor: `${CATEGORIE_LABELS[e.categorie].color}15`,
                          color: CATEGORIE_LABELS[e.categorie].color,
                        }}
                      >
                        <span
                          className="h-1.5 w-1.5 rounded-full"
                          style={{ backgroundColor: CATEGORIE_LABELS[e.categorie].color }}
                        />
                        {CATEGORIE_LABELS[e.categorie].label}
                      </span>
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3 w-3" />
                        {e.effectif}
                      </span>
                    </div>
                  </div>
                </div>
                <p className="mt-2 text-xs text-slate-500">
                  <MapPin className="mr-0.5 inline h-3 w-3 -translate-y-0.5" />
                  {e.ville}
                  {e.hub && e.hub !== e.ville ? ` · ${e.hub}` : ''}
                </p>
                <p className="mt-3 text-sm leading-relaxed text-slate-700">
                  {e.pitch}
                </p>
                {e.stack && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {e.stack.map((s) => (
                      <span
                        key={s}
                        className="inline-flex items-center rounded-md bg-slate-100 px-1.5 py-0.5 text-[10px] font-medium text-slate-700"
                      >
                        {s}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-10">
        <div className="mx-auto max-w-4xl px-6">
          <div className="rounded-2xl border border-slate-200 bg-slate-50 p-5">
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-pink-600" />
              <div className="flex-1">
                <h3 className="text-base font-bold text-slate-900">
                  Vous représentez une entreprise IA en PACA non listée ?
                </h3>
                <p className="mt-1 text-sm text-slate-600">
                  Écrivez-nous à{' '}
                  <a
                    href="mailto:contact@recruteurs.io"
                    className="font-semibold text-pink-700 hover:underline"
                  >
                    contact@recruteurs.io
                  </a>{' '}
                  pour rejoindre la cartographie. Mise à jour mensuelle.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <IaPacaCTA
        title="Une entreprise de la carte qui pourrait être votre prochain employeur ?"
        subtitle="Notre cabinet connaît la plupart de ces acteurs. Réservez 30 min pour qu'on identifie ensemble les bons leviers selon votre profil."
      />
    </Layout>
  );
}

function FilterChip({
  active,
  onClick,
  label,
  color,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  color: string;
}) {
  return (
    <button
      onClick={onClick}
      className={`flex w-full items-center gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition ${
        active
          ? 'bg-violet-100 font-semibold text-violet-900'
          : 'text-slate-700 hover:bg-slate-100'
      }`}
    >
      <span
        className="h-2 w-2 rounded-full"
        style={{ backgroundColor: color }}
      />
      {label}
    </button>
  );
}
